import React, { useState, useEffect, useCallback } from 'react';

import * as Page from 'components/page';
import { RHP } from 'providers';

export function CorporateStep7({ useRankingCorporate, t }) {
    const {
        vars,
        getCompanyRankingApplication,
        postCompanyAuditEnrollment,
    } = useRankingCorporate;
    const { application, companyApplication } = vars;
    const { steps } = application;

    const [localEnrollment, setLocalEnrollment] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Persisted state wins when it's terminal (confirmed) — webhooks may
    // have updated it while the tab was idle. Otherwise use the most
    // recent API response from this session.
    const persisted = companyApplication?.auditEnrollment;
    const enrollment =
        persisted?.status === 'confirmed'
            ? persisted
            : localEnrollment || persisted || { status: 'pending' };

    const refresh = useCallback(() => {
        getCompanyRankingApplication().catch(() => {});
    }, [getCompanyRankingApplication]);

    const enroll = useCallback(async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const redirectUrl = window.location.href;
            const result = await postCompanyAuditEnrollment({ redirectUrl });
            setLocalEnrollment(result);
        } catch (err: any) {
            console.error(err);
            setErrorMsg(
                err?.response?.data?.message ||
                err?.message ||
                t('CorporateStep7.errorGeneric')
            );
        } finally {
            setLoading(false);
        }
    }, [postCompanyAuditEnrollment, t]);

    // Auto-enroll runs ONCE per mount. Without this guard, an error path
    // would re-trigger enroll() on every render (postCompanyAuditEnrollment is
    // not memoized in useRankingCorporate, so `enroll` gets a new reference,
    // the effect re-fires, hits the same error, and we loop until the global
    // userLimiter (200 req/min) returns 429.
    const didAutoEnroll = React.useRef(false);
    useEffect(() => {
        if (didAutoEnroll.current) return;
        if (enrollment.status === 'confirmed') return;
        if (enrollment.status === 'processing' && enrollment.paymentLink) return;
        didAutoEnroll.current = true;
        enroll();
    }, [enrollment.status, enrollment.paymentLink, enroll]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refresh);
        };
    }, [refresh]);

    if (!steps || steps.step6 !== true) {
        RHP.replace('/ranking/latam/corporate/');
        return null;
    }

    const renderStatus = () => {
        if (enrollment.status === 'confirmed') {
            return (
                <div style={{ padding: '1rem', background: '#e6f7ed', borderRadius: 4 }}>
                    <strong>{t('CorporateStep7.statusConfirmedTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0' }}>
                        {t('CorporateStep7.statusConfirmedDescr')}
                    </p>
                </div>
            );
        }
        if (enrollment.status === 'processing' && enrollment.paymentLink) {
            return (
                <div style={{ padding: '1rem', background: '#fff8e1', borderRadius: 4 }}>
                    <strong>{t('CorporateStep7.statusProcessingTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {t('CorporateStep7.statusProcessingDescr')}
                    </p>
                    <a href={enrollment.paymentLink} className="btn btn-primary">
                        {t('CorporateStep7.payButton')}
                    </a>
                </div>
            );
        }
        if (errorMsg || enrollment.status === 'failed') {
            return (
                <div style={{ padding: '1rem', background: '#fdecea', borderRadius: 4 }}>
                    <strong>{t('CorporateStep7.statusFailedTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {errorMsg || t('CorporateStep7.statusFailedDescr')}
                    </p>
                    <button
                        type="button"
                        onClick={enroll}
                        disabled={loading}
                        className="btn btn-primary"
                    >
                        {t('CorporateStep7.tryAgain')}
                    </button>
                </div>
            );
        }
        return (
            <div style={{ padding: '1rem', background: '#f2f2f2', borderRadius: 4 }}>
                <strong>{t('CorporateStep7.processing')}</strong>
            </div>
        );
    };

    return (
        <Page.Page
            name="CorporateStep7"
            back="/ranking/latam/corporate"
            customBackText={t('glossary:backToRanking')}
        >
            <Page.Title>{t('CorporateStep7.title')}</Page.Title>
            <Page.Descr>{t('CorporateStep7.description')}</Page.Descr>

            <p style={{ fontSize: '0.9em', color: '#666', marginTop: 12 }}>
                {t('CorporateStep7.priceNote')}
            </p>

            <h3 style={{ marginTop: 24 }}>{t('CorporateStep7.statusSectionTitle')}</h3>
            {renderStatus()}

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <Page.Buttn
                    style={{ width: '20%' }}
                    // @ts-ignore
                    to="/ranking/latam/corporate/step6"
                >
                    <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                    <span className="text">{t('glossary:back')}</span>
                </Page.Buttn>

                <Page.Buttn
                    style={{ width: '80%' }}
                    // @ts-ignore
                    to="/ranking/latam/corporate"
                >
                    <span className="text">{t('glossary:backToRanking')}</span>
                </Page.Buttn>
            </div>
        </Page.Page>
    );
}
